import React from "react"
import type { MenuProps } from "antd" // MenuProps IS used for deriving AntMenuItem
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/free-solid-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"

// --- TYPE DEFINITIONS  ---

type AntMenuItem = MenuProps["items"] extends (infer T)[] ? T : never

/**
 * Interface for the 'extra' properties that can be passed to createMenuItem,
 * corresponding to valid Ant Design MenuItem props (excluding 'key', 'icon', 'label', 'onClick').
 */
interface CreateMenuItemExtraProps {
  disabled?: boolean
  danger?: boolean
  style?: React.CSSProperties
  className?: string
}

// --- UTILITY FUNCTIONS ---

/**
 * Creates a standard clickable menu item configuration object for Ant Design Menu.
 * @param key - Unique key for the menu item.
 * @param icon - FontAwesome icon definition.
 * @param label - Text label or ReactNode for the menu item.
 * @param onClick - Optional click handler function.
 * @param extra - Optional additional properties for the Ant Design MenuItem.
 * @returns Ant Design MenuItem configuration object.
 */
export const createMenuItem = (
  key: React.Key,
  icon: IconDefinition, // Now mandatory as this function is only for icon+label items
  label: React.ReactNode, // Now mandatory
  onClick?: (info: {
    key: React.Key
    keyPath: string[]
    domEvent: Event
  }) => void,
  extra: CreateMenuItemExtraProps = {}
): AntMenuItem => {
  return {
    key,
    icon: <FontAwesomeIcon icon={icon} />,
    label,
    onClick,
    ...extra,
  } as AntMenuItem
}

/**
 * Creates a menu item configuration with a checkbox-style indicator on the right.
 * @param key - Unique key for the menu item.
 * @param label - Text label or ReactNode for the menu item.
 * @param isChecked - Boolean indicating if the item should appear checked.
 * @param onClick - Optional click handler function.
 * @returns Ant Design MenuItem configuration object.
 */
export const createCheckMenuItem = (
  key: React.Key,
  label: React.ReactNode,
  isChecked: boolean,
  onClick?: (info: {
    key: React.Key
    keyPath: string[]
    domEvent: Event
  }) => void
): AntMenuItem =>
  ({
    key,
    label: (
      <div className="flex items-center justify-between w-full px-2 py-1">
        <span>{label}</span>
        {isChecked && (
          <FontAwesomeIcon icon={faCheck} className="text-blue-500 ml-2" />
        )}
      </div>
    ),
    onClick,
  } as AntMenuItem)

/**
 * Creates a menu item configuration with an icon, text label, and a checkbox-style indicator.
 * @param key - Unique key for the menu item.
 * @param icon - FontAwesome icon definition for the label.
 * @param label - Text label or ReactNode for the menu item.
 * @param isChecked - Boolean indicating if the item should appear checked.
 * @param onClick - Optional click handler function.
 * @returns Ant Design MenuItem configuration object.
 */
export const createIconCheckMenuItem = (
  key: React.Key,
  icon: IconDefinition,
  label: React.ReactNode,
  isChecked: boolean,
  onClick?: (info: {
    key: React.Key
    keyPath: string[]
    domEvent: Event
  }) => void
): AntMenuItem =>
  ({
    key,
    label: (
      <div className="flex items-center justify-between w-full px-2 py-1">
        <div className="flex items-center">
          <FontAwesomeIcon icon={icon} className="mr-2" />
          <span>{label}</span>
        </div>
        {isChecked && (
          <FontAwesomeIcon icon={faCheck} className="text-blue-500 ml-2" />
        )}
      </div>
    ),
    onClick,
  } as AntMenuItem)
